// Global USD Pricing — Phase 3 restructure (Feb 2026)
// Single currency, single source of truth. Regional logic removed.
// Internal plan keys (FREE / GROWTH / PRO / ENTERPRISE) match backend PlanType
// for Stripe price ID mapping and existing-customer migration.

export const USD_PRICING = {
  symbol: '$',
  code: 'USD',
  growth: { monthly: 19, annual: 15 },   // Professional
  pro:    { monthly: 49, annual: 39 },   // Business
};

// Legacy-compatible helpers (kept so existing callers don't break).
export const detectUserCurrency = async () => 'USD';
export const getRegionalPricing = () => USD_PRICING;
export const formatPrice = (price) => `${USD_PRICING.symbol}${price}`;
export const getAnnualTotal = (monthlyAnnualPrice) => monthlyAnnualPrice * 12;

// Strict SaaS quotas — must mirror backend/models/enums.py PLAN_LIMITS exactly.
// Display strings are intentionally human-friendly; numeric truth lives in backend.
export const PRICING_FEATURES = [
  { label: 'active forms',               free: 'Up to 3',     growth: 'Up to 25',    pro: 'Up to 100',    enterprise: 'Unlimited' },
  { label: 'submissions per month',      free: '100',         growth: '2,000',       pro: '10,000',       enterprise: 'Unlimited' },
  { label: 'fields per form',            free: 'Up to 100',   growth: 'Up to 250',   pro: 'Up to 500',    enterprise: 'Unlimited' },
  { label: 'file storage',               free: '50 MB',       growth: '2 GB',        pro: '10 GB',        enterprise: 'Unlimited' },
  { label: 'signed documents per month', free: 'Up to 5',     growth: 'Up to 50',    pro: 'Up to 500',    enterprise: 'Unlimited' },
  { label: 'Single-user access',         free: true,          growth: true,          pro: true,           enterprise: true },
  { label: 'analytics & reporting',      free: 'Basic',       growth: 'Standard',    pro: 'Advanced',     enterprise: 'Custom' },
  { label: 'Custom branding',            free: false,         growth: true,          pro: true,           enterprise: true },
  { label: 'Formlio branding displayed', free: true,          growth: true,          pro: false,          enterprise: false },
];

export const PLANS = {
  FREE: {
    id: 'free',
    name: 'Starter',
    description: 'For individuals getting started',
    cta: 'Start Free',
    popular: false,
  },
  GROWTH: {
    id: 'growth',
    name: 'Professional',
    description: 'For freelancers & solo creators',
    cta: 'Get Professional',
    popular: false,
  },
  PRO: {
    id: 'pro',
    name: 'Business',
    description: 'For growing teams & organizations',
    cta: 'Get Business',
    popular: true,
  },
  ENTERPRISE: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For large teams & custom needs',
    cta: 'Contact Sales',
    popular: false,
    custom_pricing: true,
  },
};

// "Why Choose Formlio" — purely marketing content.
// Phase 7 reorder: business-value-first (Modern → Growth → Integrations → Pricing → Global → Secure)
export const WHY_CHOOSE_ITEMS = [
  {
    id: 'modern',
    icon: 'rocket',
    title: 'Built for Modern Businesses',
    description: 'Formlio isn\'t just a form builder — it\'s a workflow engine. Create powerful forms, automate submissions, and connect your tools without complex setup.',
    color: 'indigo',
  },
  {
    id: 'growth',
    icon: 'target',
    title: 'Designed for Growth',
    description: 'Start free. Scale as you grow. Whether you\'re collecting leads, managing admissions, running surveys, or handling payments — Formlio grows with you.',
    color: 'amber',
  },
  {
    id: 'integrations',
    icon: 'link',
    title: 'Powerful Integrations',
    description: 'Connect your forms instantly to Google Sheets, Webhooks, CRM systems, payment gateways, and 100+ apps. Automation without technical headaches.',
    color: 'violet',
  },
  {
    id: 'pricing',
    icon: 'wallet',
    title: 'Transparent USD Pricing',
    description: 'No hidden fees. No regional games. One flat USD plan whether you\'re in Mumbai, Munich, or Manhattan. Upgrade or downgrade anytime.',
    color: 'emerald',
  },
  {
    id: 'global',
    icon: 'globe',
    title: 'Built for Global Teams',
    description: 'One USD price worldwide. Multi-currency form payments. Fast support across timezones. Built for solo creators and enterprises alike.',
    color: 'rose',
  },
  {
    id: 'secure',
    icon: 'shield',
    title: 'Secure & Reliable',
    description: 'OAuth-based authentication, encrypted data at rest and in transit, role-based access. Built on scalable cloud infrastructure. Your data stays protected.',
    color: 'cyan',
  },
];
